import { useCallback, useEffect, useMemo, useState } from 'react';
import { ResultCount } from '@jywer/ui';
import { fetchRates } from './api';
import { ProjectGroup } from './ProjectGroup';
import {
  RATE_STATUSES,
  STATUS_LABEL,
  groupByProject,
  type RateRow,
  type RateStatus,
} from './types';

const PROJECT_TYPES = ['website', 'video', 'pakkelosning', 'klippekort'];
const PROJECT_TYPE_LABEL: Record<string, string> = {
  website: 'Hjemmeside',
  video: 'Video',
  pakkelosning: 'Pakkeløsning',
  klippekort: 'Klippekort',
};

export function RatesPage() {
  const [statuses, setStatuses] = useState<RateStatus[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [rows, setRows] = useState<RateRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchRates({
        status: statuses,
        projectType: types,
        clientSearch,
        page,
        pageSize,
      });
      setRows(data.rows);
      setTotal(data.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Kunne ikke hente rates');
    } finally {
      setLoading(false);
    }
  }, [statuses, types, clientSearch, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => groupByProject(rows), [rows]);

  const toggleStatus = (s: RateStatus) => {
    setPage(1);
    setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };
  const toggleType = (t: string) => {
    setPage(1);
    setTypes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="mb-1 text-2xl font-semibold text-primary">Rates</h1>
        <p className="text-sm text-ink-2">
          Betalingsrater på tværs af alle projekter. Rediger beløb, trigger eller status inline.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded border border-line-1 bg-paper-0 px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-2">Status</span>
          <div className="flex flex-wrap gap-1">
            {RATE_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  statuses.includes(s)
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line-2 bg-paper-0 text-ink-1 hover:border-line-2'
                }`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium uppercase tracking-wide text-ink-2">
            Projekttype
          </span>
          <div className="flex flex-wrap gap-1">
            {PROJECT_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => toggleType(t)}
                className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  types.includes(t)
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line-2 bg-paper-0 text-ink-1 hover:border-line-2'
                }`}
              >
                {PROJECT_TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        </div>
        <input
          type="search"
          value={clientSearch}
          onChange={(e) => {
            setPage(1);
            setClientSearch(e.target.value);
          }}
          placeholder="Søg kunde eller projekt…"
          className="w-64 rounded border border-line-2 px-2.5 py-1.5 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <div className="ml-auto">
          <ResultCount shown={rows.length} total={total} singularNoun="rate" pluralNoun="rater" />
        </div>
      </div>

      {err ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {err}
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Henter rates…
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded border border-line-1 bg-paper-0 px-4 py-10 text-center text-ink-2">
          Ingen rates matcher filtrene.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((g) => (
            <ProjectGroup key={g.project_id} group={g} onChanged={load} />
          ))}
        </div>
      )}

      {lastPage > 1 ? (
        <div className="flex items-center justify-center gap-2 text-xs text-ink-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-line-2 px-2 py-1 disabled:opacity-40"
          >
            ← Forrige
          </button>
          <span>
            Side {page} / {lastPage}
          </span>
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            className="rounded border border-line-2 px-2 py-1 disabled:opacity-40"
          >
            Næste →
          </button>
        </div>
      ) : null}
    </div>
  );
}
